import { relations } from 'drizzle-orm'
import {
  boolean,
  decimal,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import { tstzrange } from '../common/customTypes'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getMonetaryField,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { feedbacks } from './feedback'
import { invoices } from './invoice'
import { maintenanceTasks, maintenanceTaskSteps } from './maintenanceTask'
import { opportunities } from './opportunity'
import { visitPreInfos } from './preInfo'
import { properties } from './property'
import { subscriptions } from './subscription'
import { users } from './user'
import { visitLineItems } from './visitLineItem'

export const commissionModeEnum = pgEnum('commission_mode', ['auto', 'manual'])
export const salesTypeEnum = pgEnum('sales_type', ['inbound', 'outbound'])
export const commissionTypeEnum = pgEnum('commission_type', [
  'percentage',
  'fixed',
])

export const visitStatusEnum = pgEnum('visit_status', [
  'unscheduled',
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
])

export const visitTaskStatusEnum = pgEnum('visit_task_status', [
  'pending',
  'completed',
  'skipped',
])

export const visitTaskStepStatusEnum = pgEnum('visit_task_step_status', [
  'pending',
  'completed',
  'skipped',
])

export const visitTaskBillingTypeEnum = pgEnum('visit_task_billing_type', [
  'subscription_included',
  'separately_invoiced',
])

export const eventTypeEnum = pgEnum('event_type', [
  'visit',
  'training',
  'meeting',
  'break',
  'survey',
  'other',
  'availability',
])

export const attendeeRoleEnum = pgEnum('attendee_role', [
  'lead',
  'assistant',
  'trainee',
  'instructor',
  'participant',
])

export const attendeeStatusEnum = pgEnum('attendee_status', [
  'confirmed',
  'tentative',
  'declined',
  'cancelled',
])

export const visits = pgTable(
  'visits',
  {
    ...getBaseFields(),
    subscriptionId: getNullableIdReference(() => subscriptions.id),
    opportunityId: getNullableIdReference(() => opportunities.id),
    propertyId: getNullableIdReference(() => properties.id),
    // Nullable: 'unscheduled' visits have NULL times until an officer schedules them.
    // For scheduled+ visits these are always set. See docs/multi-day-visits-refactor.md.
    startTime: getTimestampWithTimezone(),
    endTime: getTimestampWithTimezone(),
    eventType: eventTypeEnum().notNull().default('visit'),
    title: text(),
    location: text(),
    latitude: decimal({ precision: 10, scale: 8 }),
    longitude: decimal({ precision: 11, scale: 8 }),
    status: visitStatusEnum().notNull().default('scheduled'),
    startedAt: getTimestampWithTimezone(),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
    cancelReason: text(),
    internalNotes: text(),
    internalSafetyNotices: text(),
    internalSafetyNoticesReviewedAt: getTimestampWithTimezone(),
    internalSafetyNoticesReviewedBy: getNullableIdReference(() => users.id),
    internalSafetyNoticesReviewComment: text(),
    internalSalesNotes: text(),
    defectsNotes: text(),
    workContent: jsonb().$type<Array<{ key: string; label?: string }>>(),
    reportTitle: text(),
    measurementReportData: jsonb(),
    bookingConfirmationSentAt: getTimestampWithTimezone(),
    commissionMode: commissionModeEnum().notNull().default('auto'),
    salesUserId: getNullableIdReference(() => users.id),
    salesType: salesTypeEnum().notNull().default('inbound'),
    // Split sales etc.: the seller is paid via manual provisions instead (§3a)
    salesCommissionDisabled: boolean().notNull().default(false),
    internalAcquisitionData: typedJsonb('internal_acquisition_data'),
    // Stamped only by the public booking widget; staff-created visits leave it
    // NULL. The widget's conversion rate counts these rows, so the marker has
    // to be something no one can edit away — it replaces a match on the
    // "Palvelukäynnin varaus" internalNotes prefix, which officers can rewrite.
    createdVia: text().$type<'widget'>(),
    // A/B test the booking came from, e.g. 'iv-hinta:b'. Set by the public
    // booking clients; NULL when no test was running. Paired with the same
    // column on booking_analytics, it gives searches and bookings per variant.
    experiment: text(),
    // Window ("slot") bookings: the arrival window promised to the customer.
    // NULL on exact-time bookings. startTime/endTime still hold the planned
    // time, so the calendar, reminders and the double-booking constraint work
    // unchanged; the route planner moves it until `timeLockedAt` is set.
    arrivalWindowStart: getTimestampWithTimezone(),
    arrivalWindowEnd: getTimestampWithTimezone(),
    // Set once the planned time is final and has been told to the customer.
    // A window booking is flexible until then; exact-time bookings are locked
    // at creation.
    timeLockedAt: getTimestampWithTimezone(),
  },
  (table) => [
    ...getBaseIndexes('visits', table),
    index('visits_property_id_idx').on(table.propertyId),
    index('visits_subscription_id_idx').on(table.subscriptionId),
    index('visits_opportunity_id_idx').on(table.opportunityId),
    index('visits_sales_user_id_idx').on(table.salesUserId),
    index('visits_start_time_idx').on(table.startTime),
  ],
)

export const visitTasks = pgTable(
  'visit_tasks',
  {
    ...getBaseFields(),
    visitId: getIdReference(() => visits.id),
    maintenanceTaskId: getIdReference(() => maintenanceTasks.id),
    // Snapshot from template
    taskName: text().notNull(),
    taskDescription: text(),
    taskCategory: text(),
    estimatedDurationMinutes: integer(),
    // Billing
    price: getMonetaryField(),
    discountPercent: integer(),
    billingType: visitTaskBillingTypeEnum(),
    invoiceId: getNullableIdReference(() => invoices.id),
    billedAt: getTimestampWithTimezone(),
    billedByUserId: getNullableIdReference(() => users.id),
    paidAt: getTimestampWithTimezone(),
    // Completion tracking
    status: visitTaskStatusEnum().notNull().default('pending'),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
    metadata: jsonb(),
  },
  (table) => [
    ...getBaseIndexes('visit_tasks', table),
    index('visit_tasks_visit_id_idx').on(table.visitId),
    index('visit_tasks_maintenance_task_id_idx').on(table.maintenanceTaskId),
  ],
)

export const visitTaskSteps = pgTable(
  'visit_task_steps',
  {
    ...getBaseFields(),
    visitTaskId: getIdReference(() => visitTasks.id),
    maintenanceTaskStepId: getNullableIdReference(
      () => maintenanceTaskSteps.id,
    ),
    // Snapshot from maintenanceTaskSteps
    stepNumber: integer().notNull(),
    stepName: text().notNull(),
    stepDescription: text(),
    stepInstruction: text(),
    // Completion tracking
    status: visitTaskStepStatusEnum().notNull().default('pending'),
    completedAt: getTimestampWithTimezone(),
    notes: text(),
  },
  (table) => [
    ...getBaseIndexes('visit_task_steps', table),
    index('visit_task_steps_visit_task_id').on(table.visitTaskId),
  ],
)

export const visitAttendees = pgTable(
  'visit_attendees',
  {
    ...getBaseFields(),
    visitId: getIdReference(() => visits.id),
    userId: getIdReference(() => users.id),
    role: attendeeRoleEnum().notNull().default('participant'),
    status: attendeeStatusEnum().notNull().default('confirmed'),
    notes: text(),
    // Denormalized fields from visits table for constraint enforcement
    // These are automatically populated by database triggers
    visitTimeRange: tstzrange(),
    visitEventType: eventTypeEnum(),
  },
  (table) => [
    ...getBaseIndexes('visit_attendees', table),
    index('visit_attendees_visit_id_idx').on(table.visitId),
    index('visit_attendees_user_id_idx').on(table.userId),
  ],
)

export const visitCommissions = pgTable(
  'visit_commissions',
  {
    ...getBaseFields(),
    visitId: getIdReference(() => visits.id),
    userId: getIdReference(() => users.id),
    commissionType: commissionTypeEnum().notNull().default('percentage'),
    commissionValue: decimal({ precision: 10, scale: 2 }).notNull(),
    visitTaskId: getNullableIdReference(() => visitTasks.id),
    notes: text(),
  },
  (table) => [
    ...getBaseIndexes('visit_commissions', table),
    index('visit_commissions_visit_id_idx').on(table.visitId),
  ],
)

export const visitsRelations = relations(visits, ({ one, many }) => ({
  subscription: one(subscriptions, {
    fields: [visits.subscriptionId],
    references: [subscriptions.id],
  }),
  opportunity: one(opportunities, {
    fields: [visits.opportunityId],
    references: [opportunities.id],
  }),
  property: one(properties, {
    fields: [visits.propertyId],
    references: [properties.id],
  }),
  internalSafetyNoticesReviewedByUser: one(users, {
    fields: [visits.internalSafetyNoticesReviewedBy],
    references: [users.id],
    relationName: 'visitSafetyReviewer',
  }),
  salesUser: one(users, {
    fields: [visits.salesUserId],
    references: [users.id],
    relationName: 'visitSalesUser',
  }),
  preInfo: one(visitPreInfos),
  visitTasks: many(visitTasks),
  visitLineItems: many(visitLineItems),
  visitAttendees: many(visitAttendees),
  visitCommissions: many(visitCommissions),
  feedbacks: many(feedbacks),
  invoices: many(invoices),
}))

export const visitTasksRelations = relations(visitTasks, ({ one, many }) => ({
  visit: one(visits, {
    fields: [visitTasks.visitId],
    references: [visits.id],
  }),
  maintenanceTask: one(maintenanceTasks, {
    fields: [visitTasks.maintenanceTaskId],
    references: [maintenanceTasks.id],
  }),
  billedByUser: one(users, {
    fields: [visitTasks.billedByUserId],
    references: [users.id],
  }),
  invoice: one(invoices, {
    fields: [visitTasks.invoiceId],
    references: [invoices.id],
  }),
  steps: many(visitTaskSteps),
}))

export const visitTaskStepsRelations = relations(visitTaskSteps, ({ one }) => ({
  visitTask: one(visitTasks, {
    fields: [visitTaskSteps.visitTaskId],
    references: [visitTasks.id],
  }),
  maintenanceTaskStep: one(maintenanceTaskSteps, {
    fields: [visitTaskSteps.maintenanceTaskStepId],
    references: [maintenanceTaskSteps.id],
  }),
}))

export const visitAttendeesRelations = relations(visitAttendees, ({ one }) => ({
  visit: one(visits, {
    fields: [visitAttendees.visitId],
    references: [visits.id],
  }),
  user: one(users, {
    fields: [visitAttendees.userId],
    references: [users.id],
  }),
}))

export const visitCommissionsRelations = relations(
  visitCommissions,
  ({ one }) => ({
    visit: one(visits, {
      fields: [visitCommissions.visitId],
      references: [visits.id],
    }),
    user: one(users, {
      fields: [visitCommissions.userId],
      references: [users.id],
    }),
    visitTask: one(visitTasks, {
      fields: [visitCommissions.visitTaskId],
      references: [visitTasks.id],
    }),
  }),
)
